import { useEffect, useLayoutEffect, useRef } from 'react';

import { useWidgetOptionsPersistence } from './useWidgetOptionsPersistence';

/**
 * Deterministic fingerprint: sorted object keys + sorted primitive arrays (order-only diffs
 * from { ...prev, ...partial } merges must not retrigger hydrate).
 */
function stableSnapshotFingerprint(value) {
  const walk = (v) => {
    if (v === null || typeof v !== 'object') return JSON.stringify(v);
    if (Array.isArray(v)) {
      const parts = v.map(walk);
      const allPrimitive =
        v.length === 0 ||
        v.every((el) => el === null || ['string', 'number', 'boolean'].includes(typeof el));
      if (allPrimitive) return `[${[...parts].sort().join(',')}]`;
      return `[${parts.join(',')}]`;
    }
    if (Object.prototype.toString.call(v) !== '[object Object]') return JSON.stringify(String(v));
    const keys = Object.keys(v).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${walk(v[k])}`).join(',')}}`;
  };
  if (!value || typeof value !== 'object' || Object.keys(value).length === 0) return '__empty__';
  return walk(value);
}

/**
 * Canonical shape for cluster fingerprint: same fields as ClusterChart snapshot, no legacy keys,
 * coerced types (Firestore may return week/year as strings), fixed cardExpanded keys, sorted string arrays.
 */
function normalizeClusterPersistSnapshot(s) {
  if (!s || typeof s !== 'object') return null;
  const w = Number(s.selectedWeek);
  const y = Number(s.selectedYear);
  const strArr = (v) =>
    Array.isArray(v)
      ? [...v]
          .map((x) => (x == null ? '' : String(x)))
          .filter((x) => x.length > 0)
          .sort()
      : [];

  const out = {
    filtersExpanded: typeof s.filtersExpanded === 'boolean' ? s.filtersExpanded : Boolean(s.filtersExpanded),
    cardExpanded: {
      'Higher than usual': Boolean(s.cardExpanded?.['Higher than usual']),
      'Lower than usual': Boolean(s.cardExpanded?.['Lower than usual']),
      Usual: Boolean(s.cardExpanded?.['Usual']),
    },
    selectedTypes: strArr(s.selectedTypes),
    selectedVarieties: strArr(s.selectedVarieties),
    selectedSizes: strArr(s.selectedSizes),
    selectedGrades: strArr(s.selectedGrades),
    selectedPackages: strArr(s.selectedPackages),
  };
  if (Number.isFinite(w)) out.selectedWeek = w;
  if (Number.isFinite(y)) out.selectedYear = y;
  return out;
}

function savedForPersistFingerprint(widgetId, s) {
  if (!s || typeof s !== 'object') return null;
  if (widgetId !== 'cluster') return s;
  return normalizeClusterPersistSnapshot(s);
}

/**
 * One-time hydrate from Firestore `widgetOptions[widgetId]` + debounced persist on filter changes.
 * @param {string} widgetId - CHART_REGISTRY id
 * @param {unknown} resetKey - When identity changes (e.g. commodity — avoid `data` array refs that churn on parent re-render), hydration resets
 * @param {boolean} canHydrate - Chart has enough context to validate saved values
 * @param {(saved: object) => void} onHydrate - Apply saved snapshot (validate inside)
 * @param {() => object} getSnapshot - Serializable filter state
 * @param {unknown[]} persistDeps - When these change after first persist, save (e.g. filter state)
 */
export function usePersistWidgetSnapshot(
  widgetId,
  resetKey,
  canHydrate,
  onHydrate,
  getSnapshot,
  persistDeps
) {
  const { saved, persist, isReady: prefsReady } = useWidgetOptionsPersistence(widgetId);

  const hydratedRef = useRef(false);
  const skipPersistRef = useRef(true);
  const lastSavedFingerprintRef = useRef(null);

  // Scope snapshots by resetKey (e.g. commodity). When scoped, we store snapshots under a per-scope
  // map so switching commodities restores that commodity’s last selections (instead of overwriting).
  const scopeKey = resetKey == null ? null : String(resetKey);

  const persistDepsSafe = Array.isArray(persistDeps) ? persistDeps : [];

  useLayoutEffect(() => {
    hydratedRef.current = false;
    skipPersistRef.current = true;
    lastSavedFingerprintRef.current = null;
  }, [resetKey, widgetId]);

  const onHydrateRef = useRef(onHydrate);
  onHydrateRef.current = onHydrate;
  const getSnapshotRef = useRef(getSnapshot);
  getSnapshotRef.current = getSnapshot;

  useLayoutEffect(() => {
    if (!prefsReady || !canHydrate) {
      return;
    }

    const raw = saved;
    const hasRaw = raw && typeof raw === 'object' && Object.keys(raw).length > 0;

    // New format (scoped): { __byScope: { [scopeKey]: snapshot }, ... }
    const byScope = hasRaw && raw.__byScope && typeof raw.__byScope === 'object' ? raw.__byScope : null;
    const scopedSaved =
      scopeKey && byScope && typeof byScope[scopeKey] === 'object' ? byScope[scopeKey] : null;

    // Legacy format (unscoped): the snapshot is the object itself.
    // When scoped, legacy should NOT be applied (we want defaults), except for a transitional
    // legacy snapshot that was previously tagged with __scopeKey.
    const legacyScopeMatch =
      scopeKey && typeof raw?.__scopeKey === 'string' && raw.__scopeKey === scopeKey;

    const effectiveSaved = scopeKey ? (scopedSaved || (legacyScopeMatch ? raw : null)) : (hasRaw ? raw : null);
    const shouldHydrate = !!effectiveSaved;

    const fpSource = shouldHydrate ? savedForPersistFingerprint(widgetId, effectiveSaved) : null;
    const fingerprint = stableSnapshotFingerprint(fpSource);
    const prevFp = lastSavedFingerprintRef.current;

    if (fingerprint === prevFp) {
      return;
    }

    lastSavedFingerprintRef.current = fingerprint;

    if (shouldHydrate) {
      onHydrateRef.current(effectiveSaved);
    }

    hydratedRef.current = true;
  }, [prefsReady, canHydrate, saved, widgetId, resetKey, scopeKey]);

  useEffect(() => {
    if (!hydratedRef.current || !prefsReady) {
      return;
    }

    if (skipPersistRef.current) {
      skipPersistRef.current = false;
      return;
    }

    const snap = getSnapshotRef.current();
    if (!scopeKey) {
      persist(snap);
      // Stamp fingerprint so the echo of this write does not re-hydrate and undo Clear all.
      lastSavedFingerprintRef.current = stableSnapshotFingerprint(
        savedForPersistFingerprint(widgetId, snap)
      );
      return;
    }

    // Merge scoped snapshot map so each commodity keeps its own state.
    const raw = saved;
    const hasRaw = raw && typeof raw === 'object' && Object.keys(raw).length > 0;
    const prevByScope =
      hasRaw && raw.__byScope && typeof raw.__byScope === 'object' ? raw.__byScope : {};
    const scopedSnap = snap && typeof snap === 'object' ? snap : { value: snap };
    const nextByScope = {
      ...prevByScope,
      [scopeKey]: scopedSnap,
    };
    persist({
      __byScope: nextByScope,
      __scoped: true,
    });
    lastSavedFingerprintRef.current = stableSnapshotFingerprint(
      savedForPersistFingerprint(widgetId, scopedSnap)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- persistDeps lists filter fields
  }, [prefsReady, persist, widgetId, ...persistDepsSafe]);
}

export default usePersistWidgetSnapshot;
